import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, Home } from 'lucide-react';
import { watchlistService } from '../../services/watchlistService';
import { colors } from '../../config/colors';
import { Button } from '../UI/button';
import { ModalDialog } from '../UI/modal-dialog';
import { useLocale } from '../../contexts/LocaleContext';
import toast from 'react-hot-toast';

const AddressManagementModal = ({ companyId, onClose, onAddressAdded }) => {
  const { t } = useLocale();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    streetAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    isPrimary: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAddresses();
  }, [companyId]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const result = await watchlistService.getUserAddresses(userId, companyId);
      
      if (result.success) {
        setAddresses(result.data || []);
      } else {
        toast.error(result.message || 'Failed to fetch addresses');
      }
    } catch (error) {
      toast.error('Failed to fetch addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    } else if (formData.streetAddress.length > 255) {
      newErrors.streetAddress = 'Street address must be 255 characters or less';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.length > 100) {
      newErrors.city = 'City must be 100 characters or less';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    } else if (formData.country.length > 100) {
      newErrors.country = 'Country must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const result = await watchlistService.addUserAddress(userId, companyId, formData);

      if (result.success) {
        toast.success('Address added successfully');
        setFormData({
          streetAddress: '',
          city: '',
          stateProvince: '',
          postalCode: '',
          country: '',
          isPrimary: false
        });
        setShowAddForm(false);
        fetchAddresses(); // Refresh list
        if (onAddressAdded) {
          onAddressAdded(result.data);
        }
      } else {
        toast.error(result.message || 'Failed to add address');
      }
    } catch (error) {
      toast.error('Failed to add address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user')).uid;
      const result = await watchlistService.removeUserAddress(userId, companyId, addressId);

      if (result.success) {
        toast.success('Address deleted successfully');
        fetchAddresses(); // Refresh list
      } else {
        toast.error(result.message || 'Failed to delete address');
      }
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  return (
    <ModalDialog
      open
      onClose={onClose}
      title={t('addressManageTitle')}
      description={t('addressManageDescription', { companyId })}
      className="max-w-3xl"
      closeLabel={t('dialogCloseLabel')}
    >
      <div className="space-y-6">
            {/* Add Address Button */}
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full mb-6 py-4 px-6 rounded-xl border-2 border-dashed hover:border-solid transition-all flex items-center justify-center space-x-2"
                style={{
                  borderColor: colors.info[300],
                  color: colors.info[600],
                  backgroundColor: colors.info[50]
                }}
              >
                <Plus className="h-5 w-5" />
                <span className="font-medium">Add New Address</span>
              </button>
            )}

            {/* Add Address Form */}
            {showAddForm && (
              <form onSubmit={handleSubmit} className="mb-6 p-6 rounded-xl border-2" style={{ borderColor: colors.info[200], backgroundColor: colors.info[50] }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>
                  New Address
                </h3>
                
                <div className="space-y-4">
                  {/* Street Address */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleChange}
                      maxLength={255}
                      placeholder="123 Main Street"
                      className={`input ${errors.streetAddress ? 'border-red-500' : ''}`}
                    />
                    {errors.streetAddress && (
                      <p className="mt-1 text-sm text-red-600">{errors.streetAddress}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        maxLength={100}
                        placeholder="New York"
                        className={`input ${errors.city ? 'border-red-500' : ''}`}
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                      )}
                    </div>

                    {/* State/Province */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                        State/Province
                      </label>
                      <input
                        type="text"
                        name="stateProvince"
                        value={formData.stateProvince}
                        onChange={handleChange}
                        maxLength={50}
                        placeholder="NY"
                        className="input"
                      />
                    </div>

                    {/* Postal Code */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                        Postal Code
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        maxLength={20}
                        placeholder="10001"
                        className="input"
                      />
                    </div>

                    {/* Country */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: colors.text.secondary }}>
                        Country *
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        maxLength={100}
                        placeholder="United States"
                        className={`input ${errors.country ? 'border-red-500' : ''}`}
                      />
                      {errors.country && (
                        <p className="mt-1 text-sm text-red-600">{errors.country}</p>
                      )}
                    </div>
                  </div>

                  {/* Primary Address */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isPrimary"
                      name="isPrimary"
                      checked={formData.isPrimary}
                      onChange={handleChange}
                      className="rounded"
                      style={{ accentColor: colors.info[500] }}
                    />
                    <label htmlFor="isPrimary" className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                      Set as primary address
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAddForm(false)}
                    disabled={submitting}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Address
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Address List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray[400] }} />
                <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
                  No Addresses Yet
                </h3>
                <p style={{ color: colors.text.secondary }}>
                  Add addresses to create routes and track locations
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map((address) => (
                  <div
                    key={address.addressId}
                    className="p-4 rounded-xl border hover:shadow-md transition-all"
                    style={{ borderColor: colors.border.light }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {address.isPrimary && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: colors.success[100],
                                color: colors.success[700]
                              }}
                            >
                              <Home className="h-3 w-3 mr-1" />
                              Primary
                            </span>
                          )}
                        </div>
                        <p className="font-medium mb-1" style={{ color: colors.text.primary }}>
                          {address.streetAddress}
                        </p>
                        <p className="text-sm" style={{ color: colors.text.secondary }}>
                          {[address.city, address.stateProvince, address.postalCode, address.country]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(address.addressId)}
                        className="ml-4 p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </div>
    </ModalDialog>
  );
};

export default AddressManagementModal;
















